import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { httpClient } from '@/lib/api/httpClient';

const REASON_CATEGORIES = [
  'Health / Medical Emergency',
  'Personal / Family Emergency',
  'Out of Station / Travel Issue',
  'Commute / Vehicle Breakdown',
  'Forgot to Mark Attendance / Intimate',
  'Others / Unforeseen Circumstances'
];

export function AbsenceReasonPromptModal({
  unexcusedDays = [],
  onClose,
  onSubmitSuccess
}) {
  const [selectedDate, setSelectedDate] = useState(
    unexcusedDays[0]?.date || ''
  );
  const [category, setCategory] = useState(REASON_CATEGORIES[0]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (unexcusedDays.length > 0 && (!selectedDate || !unexcusedDays.some(d => d.date === selectedDate))) {
      setSelectedDate(unexcusedDays[0].date);
    }
  }, [unexcusedDays, selectedDate]);

  const currentItem =
    unexcusedDays.find((d) => d.date === selectedDate) || unexcusedDays[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate) {
      setError('Please select an absent date.');
      return;
    }
    const cleanReason = String(reason || '').trim();
    if (!cleanReason) {
      setError('Please enter a detailed reason for your absence.');
      return;
    }
    if (cleanReason.length < 5) {
      setError('Please provide at least 5 characters explaining your absence.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const res = await httpClient('/api/attendance/submit-absence-reason', {
        method: 'POST',
        body: JSON.stringify({
          date: selectedDate,
          reasonType: category,
          reason: cleanReason
        })
      });

      if (res?.success) {
        setSuccessMsg(
          'Reason submitted successfully! Recorded as an Intimation and sent for review.'
        );
        setTimeout(() => {
          if (onSubmitSuccess) {
            onSubmitSuccess(selectedDate);
          } else if (onClose) {
            onClose();
          }
        }, 1200);
      } else {
        setError(res?.message || 'Failed to submit reason. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred while submitting.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnooze = () => {
    try {
      sessionStorage.setItem('wt_snooze_absence_prompt', 'true');
    } catch (_) {}
    if (onClose) onClose();
  };

  return createPortal(
    <div
      className="absence-prompt-layer"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(15, 23, 42, 0.72)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        animation: 'absenceFadeIn 0.25s ease-out'
      }}
    >
      <div
        className="absence-prompt-card"
        style={{
          width: '100%',
          maxWidth: '540px',
          background: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(239, 68, 68, 0.2)',
          border: '1.5px solid rgba(248, 113, 113, 0.3)',
          overflow: 'hidden',
          animation: 'absenceScaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Top Warning Accent Bar */}
        <div
          style={{
            height: '5px',
            background: 'linear-gradient(90deg, #f59e0b 0%, #ef4444 50%, #dc2626 100%)'
          }}
        />

        {/* Modal Header */}
        <div
          style={{
            padding: '24px 28px 18px 28px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            borderBottom: '1px solid #f1f5f9'
          }}
        >
          <div
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fee2e2 100%)',
              border: '1px solid #fecaca',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)'
            }}
          >
            ⚠️
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: '19px',
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.3px'
                }}
              >
                Absence Reason Required
              </h2>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#b91c1c',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px'
                }}
              >
                Unexcused
              </span>
            </div>
            <p
              style={{
                margin: '5px 0 0 0',
                fontSize: '13px',
                color: '#64748b',
                lineHeight: '1.45'
              }}
            >
              You were marked <strong>Absent</strong> without any prior Leave or Intimation submitted. Please provide the reason below to notify management.
            </p>
          </div>

          <button
            type="button"
            onClick={handleSnooze}
            title="Remind me later"
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 700,
              padding: '4px',
              lineHeight: 1,
              borderRadius: '8px',
              transition: 'color 0.15s ease'
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '20px 28px 24px 28px' }}>
          {successMsg ? (
            <div
              style={{
                padding: '20px',
                background: '#f0fdf4',
                border: '1.5px solid #bbf7d0',
                borderRadius: '16px',
                textAlign: 'center',
                color: '#15803d',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '32px' }}>✓</span>
              <strong style={{ fontSize: '15px' }}>Submitted Successfully</strong>
              <span style={{ fontSize: '13px', color: '#166534' }}>{successMsg}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Absent Date Badge / Selector */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#334155',
                    marginBottom: '6px'
                  }}
                >
                  Absent Date <span style={{ color: '#ef4444' }}>*</span>
                </label>
                {unexcusedDays.length > 1 ? (
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#0f172a',
                      background: '#fff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {unexcusedDays.map((d) => (
                      <option key={d.date} value={d.date}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 14px',
                      background: '#fff1f2',
                      border: '1.5px solid #fecdd3',
                      borderRadius: '12px',
                      color: '#9f1239',
                      fontWeight: 700,
                      fontSize: '14px'
                    }}
                  >
                    <span>📅</span>
                    <span>{currentItem?.label || currentItem?.formattedDate || selectedDate}</span>
                  </div>
                )}
              </div>

              {/* Reason Category */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#334155',
                    marginBottom: '6px'
                  }}
                >
                  Reason Category <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#0f172a',
                    background: '#fff',
                    boxSizing: 'border-box'
                  }}
                >
                  {REASON_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Detailed Reason */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#334155',
                    marginBottom: '6px'
                  }}
                >
                  Reason Details <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why you were absent and why prior intimation was not submitted..."
                  required
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '13.5px',
                    fontFamily: 'inherit',
                    color: '#0f172a',
                    background: '#fff',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                  }}
                />
              </div>

              {error && (
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '10px',
                    color: '#b91c1c',
                    fontSize: '12.5px',
                    fontWeight: 600
                  }}
                >
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '8px'
                }}
              >
                <button
                  type="button"
                  onClick={handleSnooze}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#64748b',
                    fontSize: '13.5px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Fill Later
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
                    color: '#ffffff',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 14px rgba(220, 38, 38, 0.35)',
                    opacity: submitting ? 0.7 : 1
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Reason'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
